import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { settings, data, tags as tagsApi } from "../services/api";
import offerup from "../services/offerup";
import { toast } from "../components/Toast";
import EbaySettings from "../components/EbaySettings";
import {
  BackIcon,
  CloseIcon,
  DownloadIcon,
  UploadIcon,
  SettingsIcon,
  HomeIcon,
  CameraIcon,
  UserIcon,
} from "../components/Icons";

const TAG_COLORS = [
  "#6B7280", // Gray
  "#EF4444", // Red
  "#F97316", // Orange
  "#F59E0B", // Amber
  "#84CC16", // Lime
  "#22C55E", // Green
  "#14B8A6", // Teal
  "#06B6D4", // Cyan
  "#3B82F6", // Blue
  "#8B5CF6", // Violet
  "#EC4899", // Pink
];

function Settings() {
  const navigate = useNavigate();
  const [containerTypes, setContainerTypes] = useState([]);
  const [locationTypes, setLocationTypes] = useState([]);
  const [newContainerType, setNewContainerType] = useState("");
  const [newLocationType, setNewLocationType] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [pendingImportFile, setPendingImportFile] = useState(null);
  const fileInputRef = useRef(null);

  // Tags state
  const [tags, setTags] = useState([]);
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState(TAG_COLORS[0]);
  const [editingTag, setEditingTag] = useState(null);
  const [editTagName, setEditTagName] = useState("");
  const [editTagColor, setEditTagColor] = useState("");

  // OfferUp Mac agent job queue state
  const [offerUpJobs, setOfferUpJobs] = useState([]);
  const [offerUpJobsLoading, setOfferUpJobsLoading] = useState(true);
  const [offerUpJobsError, setOfferUpJobsError] = useState(null);

  useEffect(() => {
    loadSettings();
    loadTags();
    loadOfferUpJobs();
  }, []);

  const loadSettings = async () => {
    try {
      const { data } = await settings.get();
      setContainerTypes(data.containerTypes || []);
      setLocationTypes(data.locationTypes || []);
    } catch (err) {
      console.error("Failed to load settings:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadTags = async () => {
    try {
      const { data } = await tagsApi.list();
      setTags(data);
    } catch (err) {
      console.error("Failed to load tags:", err);
    }
  };

  const addTag = async () => {
    const name = newTagName.trim();
    if (!name) return;

    try {
      await tagsApi.create({ name, color: newTagColor });
      setNewTagName("");
      setNewTagColor(TAG_COLORS[0]);
      loadTags();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to create tag");
    }
  };

  const startEditTag = (tag) => {
    setEditingTag(tag.id);
    setEditTagName(tag.name);
    setEditTagColor(tag.color);
  };

  const saveEditTag = async () => {
    if (!editingTag) return;

    try {
      await tagsApi.update(editingTag, {
        name: editTagName,
        color: editTagColor,
      });
      setEditingTag(null);
      loadTags();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to update tag");
    }
  };

  const deleteTag = async (id) => {
    if (
      !window.confirm("Delete this tag? It will be removed from all entities.")
    )
      return;

    try {
      await tagsApi.delete(id);
      loadTags();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to delete tag");
    }
  };

  const saveSettings = async (updates, revert) => {
    setSaving(true);
    try {
      await settings.update(updates);
    } catch (err) {
      console.error("Failed to save settings:", err);
      toast.error("Failed to save settings");
      // Roll the optimistic local update back so the UI matches the server.
      if (revert) revert();
    } finally {
      setSaving(false);
    }
  };

  const addContainerType = () => {
    const value = newContainerType.trim().toLowerCase();
    if (value && !containerTypes.includes(value)) {
      const updated = [...containerTypes, value];
      setContainerTypes(updated);
      setNewContainerType("");
      saveSettings({ containerTypes: updated });
    }
  };

  const removeContainerType = (type) => {
    if (!window.confirm(`Remove the "${formatLabel(type)}" container type?`))
      return;

    const previous = containerTypes;
    const updated = containerTypes.filter((t) => t !== type);
    setContainerTypes(updated);
    saveSettings({ containerTypes: updated }, () =>
      setContainerTypes(previous),
    );
  };

  const addLocationType = () => {
    const value = newLocationType.trim().toLowerCase();
    if (value && !locationTypes.includes(value)) {
      const updated = [...locationTypes, value];
      setLocationTypes(updated);
      setNewLocationType("");
      saveSettings({ locationTypes: updated });
    }
  };

  const removeLocationType = (type) => {
    if (!window.confirm(`Remove the "${formatLabel(type)}" location type?`))
      return;

    const previous = locationTypes;
    const updated = locationTypes.filter((t) => t !== type);
    setLocationTypes(updated);
    saveSettings({ locationTypes: updated }, () => setLocationTypes(previous));
  };

  const formatLabel = (type) => {
    return type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, " ");
  };

  const handleExport = async () => {
    // Fetch through the authenticated client (Authorization header) and save
    // the blob — the API never reads a ?token= query param.
    try {
      const res = await data.export();
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `frugalstack-export-${Date.now()}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to export data:", err);
      toast.error("Failed to export data");
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setPendingImportFile(file);
      setShowImportConfirm(true);
    }
    // Reset input so same file can be selected again
    e.target.value = "";
  };

  const handleImportConfirm = async () => {
    if (!pendingImportFile) return;

    setImporting(true);
    setShowImportConfirm(false);

    try {
      const result = await data.import(pendingImportFile);
      alert(
        `Import successful!\n\nImported:\n- ${result.data.imported.entities} entities\n- ${result.data.imported.tags} tags\n- ${result.data.imported.qrCodes} QR codes\n- ${result.data.imported.customViews} custom views`,
      );
      // Reload settings after import
      loadSettings();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to import data");
    } finally {
      setImporting(false);
      setPendingImportFile(null);
    }
  };

  const loadOfferUpJobs = async () => {
    setOfferUpJobsLoading(true);
    setOfferUpJobsError(null);
    try {
      const jobs = await offerup.listJobs();
      setOfferUpJobs((jobs || []).slice(0, 10));
    } catch (err) {
      console.error("Failed to load OfferUp jobs:", err);
      setOfferUpJobsError("Failed to load jobs");
    } finally {
      setOfferUpJobsLoading(false);
    }
  };

  const handleCancelOfferUpJob = async (jobId) => {
    if (!window.confirm("Cancel this queued job?")) return;
    try {
      await offerup.cancelJob(jobId);
      loadOfferUpJobs();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to cancel job");
    }
  };

  const getOfferUpJobTitle = (job) => {
    try {
      const payload =
        typeof job.payload === "string"
          ? JSON.parse(job.payload)
          : job.payload;
      return payload?.title || "";
    } catch (e) {
      return "";
    }
  };

  const getOfferUpJobBadge = (status) => {
    const badges = {
      queued: "badge-warning",
      claimed: "badge-product",
      done: "badge-success",
      failed: "badge-danger",
    };
    return badges[status] || "";
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", paddingBottom: "80px" }}>
      <div className="header">
        <div className="header-content">
          <button
            onClick={() => navigate("/")}
            className="btn btn-ghost btn-icon"
          >
            <BackIcon size={20} />
          </button>
          <div className="header-text">
            <h1 className="header-title">Settings</h1>
            <p className="header-subtitle">
              Configure your application preferences
            </p>
          </div>
          {saving && (
            <div className="header-actions-right">
              <span className="text-muted">Saving...</span>
            </div>
          )}
        </div>
      </div>

      <div className="container">
        {/* Container Types */}
        <div className="card" style={{ marginBottom: "var(--spacing-lg)" }}>
          <h3 style={{ marginBottom: "var(--spacing-md)" }}>Container Types</h3>
          <p
            className="text-secondary"
            style={{ marginBottom: "var(--spacing-md)" }}
          >
            Define the types of containers available when creating new
            containers.
          </p>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "var(--spacing-sm)",
              marginBottom: "var(--spacing-md)",
            }}
          >
            {containerTypes.map((type) => (
              <div
                key={type}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "var(--spacing-xs)",
                  padding: "var(--spacing-xs) var(--spacing-sm)",
                  background: "var(--bg-tertiary)",
                  borderRadius: "var(--radius-sm)",
                }}
              >
                <span>{formatLabel(type)}</span>
                <button
                  onClick={() => removeContainerType(type)}
                  className="btn btn-ghost btn-icon"
                  style={{
                    padding: "2px 6px",
                    fontSize: "0.75rem",
                    minWidth: "auto",
                  }}
                >
                  <CloseIcon size={12} />
                </button>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", gap: "var(--spacing-sm)" }}>
            <input
              type="text"
              placeholder="New container type"
              value={newContainerType}
              onChange={(e) => setNewContainerType(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addContainerType()}
              style={{ flex: 1 }}
            />
            <button onClick={addContainerType} className="btn btn-primary">
              Add
            </button>
          </div>
        </div>

        {/* Location Types */}
        <div className="card">
          <h3 style={{ marginBottom: "var(--spacing-md)" }}>Location Types</h3>
          <p
            className="text-secondary"
            style={{ marginBottom: "var(--spacing-md)" }}
          >
            Define the types of locations available when creating new locations.
          </p>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "var(--spacing-sm)",
              marginBottom: "var(--spacing-md)",
            }}
          >
            {locationTypes.map((type) => (
              <div
                key={type}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "var(--spacing-xs)",
                  padding: "var(--spacing-xs) var(--spacing-sm)",
                  background: "var(--bg-tertiary)",
                  borderRadius: "var(--radius-sm)",
                }}
              >
                <span>{formatLabel(type)}</span>
                <button
                  onClick={() => removeLocationType(type)}
                  className="btn btn-ghost btn-icon"
                  style={{
                    padding: "2px 6px",
                    fontSize: "0.75rem",
                    minWidth: "auto",
                  }}
                >
                  <CloseIcon size={12} />
                </button>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", gap: "var(--spacing-sm)" }}>
            <input
              type="text"
              placeholder="New location type"
              value={newLocationType}
              onChange={(e) => setNewLocationType(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addLocationType()}
              style={{ flex: 1 }}
            />
            <button onClick={addLocationType} className="btn btn-primary">
              Add
            </button>
          </div>
        </div>

        {/* Tags */}
        <div className="card" style={{ marginBottom: "var(--spacing-lg)" }}>
          <h3 style={{ marginBottom: "var(--spacing-md)" }}>Tags</h3>
          <p
            className="text-secondary"
            style={{ marginBottom: "var(--spacing-md)" }}
          >
            Create and manage tags to organize your entities.
          </p>

          {/* Existing Tags */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--spacing-sm)",
              marginBottom: "var(--spacing-md)",
            }}
          >
            {tags.map((tag) => (
              <div
                key={tag.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "var(--spacing-sm)",
                  padding: "var(--spacing-sm)",
                  background: "var(--bg-tertiary)",
                  borderRadius: "var(--radius-sm)",
                }}
              >
                {editingTag === tag.id ? (
                  <>
                    <div
                      style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}
                    >
                      {TAG_COLORS.map((color) => (
                        <button
                          key={color}
                          onClick={() => setEditTagColor(color)}
                          style={{
                            width: "24px",
                            height: "24px",
                            borderRadius: "50%",
                            background: color,
                            border:
                              editTagColor === color
                                ? "2px solid white"
                                : "none",
                            boxShadow:
                              editTagColor === color
                                ? `0 0 0 2px ${color}`
                                : "none",
                            cursor: "pointer",
                          }}
                        />
                      ))}
                    </div>
                    <input
                      type="text"
                      value={editTagName}
                      onChange={(e) => setEditTagName(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && saveEditTag()}
                      style={{ flex: 1, minWidth: "100px" }}
                      autoFocus
                    />
                    <button
                      onClick={saveEditTag}
                      className="btn btn-primary btn-sm"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingTag(null)}
                      className="btn btn-ghost btn-sm"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <div
                      style={{
                        width: "16px",
                        height: "16px",
                        borderRadius: "50%",
                        background: tag.color,
                        flexShrink: 0,
                      }}
                    />
                    <span style={{ flex: 1 }}>{tag.name}</span>
                    <span
                      className="text-muted"
                      style={{ fontSize: "0.75rem" }}
                    >
                      {tag.entity_count} items
                    </span>
                    <button
                      onClick={() => startEditTag(tag)}
                      className="btn btn-ghost btn-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deleteTag(tag.id)}
                      className="btn btn-ghost btn-icon"
                      style={{
                        padding: "2px 6px",
                        fontSize: "0.75rem",
                        minWidth: "auto",
                      }}
                    >
                      <CloseIcon size={12} />
                    </button>
                  </>
                )}
              </div>
            ))}
            {tags.length === 0 && (
              <p className="text-muted" style={{ fontSize: "0.875rem" }}>
                No tags created yet.
              </p>
            )}
          </div>

          {/* Add New Tag */}
          <div
            style={{
              display: "flex",
              gap: "var(--spacing-sm)",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", gap: "4px" }}>
              {TAG_COLORS.map((color) => (
                <button
                  key={color}
                  onClick={() => setNewTagColor(color)}
                  style={{
                    width: "24px",
                    height: "24px",
                    borderRadius: "50%",
                    background: color,
                    border: newTagColor === color ? "2px solid white" : "none",
                    boxShadow:
                      newTagColor === color ? `0 0 0 2px ${color}` : "none",
                    cursor: "pointer",
                  }}
                />
              ))}
            </div>
            <input
              type="text"
              placeholder="New tag name"
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addTag()}
              style={{ flex: 1, minWidth: "120px" }}
            />
            <button onClick={addTag} className="btn btn-primary">
              Add Tag
            </button>
          </div>
        </div>

        {/* Marketplace Mac Agent (OfferUp + Facebook via iPhone Mirroring) */}
        <div className="card" style={{ marginBottom: "var(--spacing-lg)" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "var(--spacing-md)",
            }}
          >
            <h3 style={{ margin: 0 }}>Marketplace Mac Agent</h3>
            <button
              onClick={loadOfferUpJobs}
              className="btn btn-ghost btn-sm"
              disabled={offerUpJobsLoading}
            >
              {offerUpJobsLoading ? "Loading..." : "Refresh"}
            </button>
          </div>
          <p
            className="text-secondary"
            style={{ marginBottom: "var(--spacing-md)" }}
          >
            OfferUp and Facebook Marketplace posting runs through the Mac
            agent: queued jobs are executed by a Mac driving the OfferUp and
            Seller iOS apps via iPhone Mirroring. No credentials are needed
            here. Recent jobs:
          </p>

          {offerUpJobsError ? (
            <p className="text-muted" style={{ fontSize: "0.875rem" }}>
              {offerUpJobsError}
            </p>
          ) : offerUpJobs.length === 0 ? (
            <p className="text-muted" style={{ fontSize: "0.875rem" }}>
              {offerUpJobsLoading
                ? "Loading jobs..."
                : "No jobs yet. Queue one from a product's marketplace button."}
            </p>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "var(--spacing-sm)",
              }}
            >
              {offerUpJobs.map((job) => (
                <div
                  key={job.id}
                  style={{
                    padding: "var(--spacing-sm) var(--spacing-md)",
                    background: "var(--bg-tertiary)",
                    borderRadius: "var(--radius-sm)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "var(--spacing-sm)",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                        color: "var(--text-muted)",
                        flexShrink: 0,
                      }}
                    >
                      {job.marketplace === "facebook" ? "FB" : "OU"}{" "}
                      {job.action}
                    </span>
                    <span
                      style={{
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {getOfferUpJobTitle(job) || `Job #${job.id}`}
                    </span>
                    <span
                      className="text-muted"
                      style={{ fontSize: "0.75rem", flexShrink: 0 }}
                    >
                      {job.created_at
                        ? new Date(job.created_at).toLocaleString()
                        : ""}
                    </span>
                    <span
                      className={`badge ${getOfferUpJobBadge(job.status)}`}
                      style={{ flexShrink: 0 }}
                    >
                      {job.status}
                    </span>
                    {job.status === "queued" && (
                      <button
                        onClick={() => handleCancelOfferUpJob(job.id)}
                        className="btn btn-ghost btn-sm"
                        style={{ flexShrink: 0 }}
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                  {job.status === "failed" && job.last_error && (
                    <div
                      style={{
                        marginTop: "var(--spacing-xs)",
                        fontSize: "0.75rem",
                        color: "var(--danger, #ef4444)",
                        wordBreak: "break-word",
                      }}
                    >
                      {job.last_error}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* eBay Integration */}
        <EbaySettings />

        {/* Data Management */}
        <div className="card">
          <h3 style={{ marginBottom: "var(--spacing-md)" }}>Data Management</h3>
          <p
            className="text-secondary"
            style={{ marginBottom: "var(--spacing-lg)" }}
          >
            Export your data for backup or import data from a previous export.
          </p>

          <div
            style={{
              display: "flex",
              gap: "var(--spacing-md)",
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={handleExport}
              className="btn btn-secondary"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "var(--spacing-xs)",
              }}
            >
              <DownloadIcon size={16} /> Export Data
            </button>
            <button
              onClick={handleImportClick}
              className="btn btn-secondary"
              // The server's /data/import endpoint is a 501 stub; keep the
              // destructive-sounding flow unreachable until it exists.
              disabled
              title="Import is not yet available"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "var(--spacing-xs)",
              }}
            >
              <UploadIcon size={16} /> Import Data (not yet available)
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".zip"
              onChange={handleFileSelect}
              style={{ display: "none" }}
            />
          </div>

          <p
            className="text-muted"
            style={{ marginTop: "var(--spacing-md)", fontSize: "0.875rem" }}
          >
            Export downloads a JSON file containing all your entities, tags,
            QR codes, and sales. Importing a backup is not yet supported.
          </p>
        </div>
      </div>

      {/* Import Confirmation Modal */}
      {showImportConfirm && (
        <div
          className="modal-overlay"
          onClick={() => {
            setShowImportConfirm(false);
            setPendingImportFile(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setShowImportConfirm(false);
              setPendingImportFile(null);
            } else if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              handleImportConfirm();
            }
          }}
          tabIndex={0}
          ref={(el) => el?.focus()}
        >
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Import Data?</h2>
              <button
                onClick={() => {
                  setShowImportConfirm(false);
                  setPendingImportFile(null);
                }}
                className="btn btn-ghost btn-icon"
              >
                <CloseIcon size={18} />
              </button>
            </div>
            <div className="modal-body">
              <p>
                <strong>Warning:</strong> Importing will replace all your
                existing data including entities, tags, QR codes, and custom
                views.
              </p>
              <p className="text-muted" style={{ fontSize: "0.875rem" }}>
                This action cannot be undone. Make sure you have a backup of
                your current data.
              </p>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => {
                  setShowImportConfirm(false);
                  setPendingImportFile(null);
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button onClick={handleImportConfirm} className="btn btn-danger">
                Import & Replace Data
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default Settings;
